<script lang="ts">
  import ShopifyImg from '$components/shopify-img.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import type { ShopifyImage } from '$types/shopify';
  import { fade } from 'svelte/transition';
  import { createEventDispatcher, onMount } from 'svelte';
  import { cn } from '$lib/utils';
  import SliderButton from '$components/ui/slider-button.svelte';

  export let selectedMedia: ShopifyImage | string | undefined = undefined;
  export let nextDisabled: boolean | undefined = undefined;
  export let prevDisabled: boolean | undefined = undefined;

  const dispatch = createEventDispatcher<{ close: undefined; next: undefined; prev: undefined }>();

  const uniqueId =
    typeof selectedMedia === 'string'
      ? undefined
      : selectedMedia?.id || `${Date.now() + Math.random()}`;

  function onClosePreview() {
    dispatch('close');
  }

  function onNextClick() {
    dispatch('next');
  }

  function onPrevClick() {
    dispatch('prev');
  }

  function scrollImageIntoView(el: HTMLImageElement) {
    if (!el) return;

    const containerElement = document.getElementById('preview');

    const imageHeight = el.height;
    const imageWidth = el.width;
    const containerHeight = containerElement?.scrollHeight || 0;
    const containerWidth = containerElement?.clientWidth || 0;

    let fromTop = 0;
    let fromLeft = 0;

    if (imageHeight > containerHeight) {
      fromTop = (imageHeight - containerHeight) / 2;
    }

    if (imageWidth > containerWidth) {
      fromLeft = (imageWidth - containerWidth) / 2;
    }

    if (containerElement) {
      containerElement.scrollTo({
        top: fromTop,
        left: fromLeft,
        behavior: 'instant'
      });
    }
  }

  onMount(() => {
    const imageElement = uniqueId ? (document.getElementById(uniqueId) as HTMLImageElement) : null;

    if (imageElement) {
      imageElement.onload = function () {
        const interval = setInterval(() => {
          if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
            clearInterval(interval);
            scrollImageIntoView(imageElement);
          }
        }, 20);
      };
    }
  });
</script>

<div
  class={cn(
    'bg-background fixed z-[55] top-0 left-0 w-full h-full overflow-auto flex items-center hide-scrollbar',
    {
      hidden: !selectedMedia
    }
  )}
  id="preview"
  transition:fade={{ duration: $reducedMotion ? 0 : 500 }}
>
  <button
    class="fixed lg:top-8 lg:right-8 top-4 right-4 z-[55] bg-highlight/64 h-7 w-7 rounded-xs flex items-center justify-center"
    on:click={() => onClosePreview()}
  >
    <Icon icon="cross" size="sm" class="text-primary" />
  </button>

  <div class="w-full fixed flex justify-between top-1/2 right-0 z-[60]">
    <SliderButton
      class="lg:ml-8 ml-4"
      variant="ghost"
      icon="chevron-left-bold"
      disabled={prevDisabled}
      on:click={() => {
        onPrevClick();
      }}
    />

    <SliderButton
      class="lg:mr-8 mr-4"
      variant="ghost"
      icon="chevron-right-bold"
      disabled={nextDisabled}
      on:click={() => {
        onNextClick();
      }}
    />
  </div>

  {#if typeof selectedMedia === 'object' && selectedMedia?.__typename === 'Image'}
    <ShopifyImg
      loading="eager"
      id={uniqueId}
      src={selectedMedia.url}
      alt={selectedMedia.altText}
      draggable="false"
      class="w-auto max-w-[unset] mx-auto h-full lg:w-full lg:h-auto"
    />
  {:else}
    <video
      src={selectedMedia + '#t=0.001'}
      preload="metadata"
      autoplay={true}
      loop
      muted
      playsinline
      class="min-w-full h-full object-contain"
      on:click={(e) => {
        const target = e.target;
        if (target && target instanceof HTMLVideoElement) {
          target.paused ? target.play() : target.pause();
        }
      }}
    />
  {/if}
</div>
